import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../icons/Icon.js';
import { cx } from '../utils/cx.js';

/**
 * トースト（C-15）。下から出す（モバイルではタブバーの上）。読み上げ対応（live region）。
 * 成功通知はトーストだけに頼らない — 同じ結果を一覧の状態にも反映すること（05 §3.8-3）。
 */
export type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface ToastApi {
  success(message: string): void;
  error(message: string): void;
  info(message: string): void;
  show(kind: ToastKind, message: string, durationMs?: number): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setItems((cur) => cur.filter((t) => t.id !== id)), []);

  const show = useCallback(
    (kind: ToastKind, message: string, durationMs?: number) => {
      const id = ++seq.current;
      setItems((cur) => [...cur.slice(-3), { id, kind, message }]);
      const ms = durationMs ?? (kind === 'error' ? 8000 : 3500);
      window.setTimeout(() => dismiss(id), ms);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m) => show('success', m),
      error: (m) => show('error', m),
      info: (m) => show('info', m),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4"
        style={{ bottom: 'var(--toast-bottom, 24px)' }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.kind === 'error' ? 'alert' : 'status'}
            aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
            className={cx(
              'pointer-events-auto flex w-full max-w-lg items-start gap-3 rounded-card border px-4 py-3 text-callout shadow-elev-2',
              'bg-card text-fg border-line-strong',
              t.kind === 'error' && 'border-danger',
              t.kind === 'success' && 'border-success',
            )}
          >
            <span className={cx('mt-0.5 shrink-0', t.kind === 'error' && 'text-danger', t.kind === 'success' && 'text-success', t.kind === 'info' && 'text-fg-secondary')}>
              <Icon name={t.kind === 'error' ? 'error' : t.kind === 'success' ? 'check_circle' : 'info'} size={20} />
            </span>
            <span className="min-w-0 flex-1 wrap-name">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="-m-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-pill text-fg-secondary hover:bg-sunken"
              aria-label="通知を閉じる"
            >
              <Icon name="close" size={20} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('ToastProvider の外で useToast() が呼ばれました');
  return api;
}
